import { animated, AnimatedComponent } from '@react-spring/web';
import React from 'react';

import { useThemeDefinition } from '@tanium/react-theme-definition';
import { ThemeDefinition } from '@tanium/react-theme-definition/dist/useThemeDefinition';

import { OpacityBlendBackgroundColor } from '../../utils/ColorDefinitions';
import { convertHexColorWithOpacity } from '../../utils/convertColorWithOpacity';

interface PathOwnProps {
  /**
   * A theme definition for the fill color.
   */
  fill?: ThemeDefinition<React.SVGAttributes<SVGPathElement>['fill']>;
  /**
   * Opacity value needed to caculate the fill.
   */
  opacity?: React.SVGAttributes<SVGPathElement>['opacity'];
  /**
   * A theme definition for the stroke color.
   */
  stroke?: ThemeDefinition<React.SVGAttributes<SVGPathElement>['fill']>;
}

export type PathProps = PathOwnProps &
  Omit<React.ComponentPropsWithoutRef<AnimatedComponent<'path'>>, keyof PathOwnProps | 'css'>;

const undefinedThemeDefinition = () => undefined;

/**
 * A component that renders a path, with support for certain theme-aware props.
 */
export const Path = ({
  fill: fillProp = undefinedThemeDefinition,
  stroke: strokeProp = undefinedThemeDefinition,
  opacity,
  ...props
}: PathProps): JSX.Element => {
  const [fill, stroke, backgroundFill] = useThemeDefinition([fillProp, strokeProp, OpacityBlendBackgroundColor]);

  const opacityValue = typeof opacity === 'string' ? parseFloat(opacity) : opacity;
  const fillWithOpacity =
    fill && fill !== 'none' && backgroundFill ? convertHexColorWithOpacity(fill, backgroundFill, opacityValue) : fill;
  return (
    <animated.path
      {...props}
      fill={fillWithOpacity}
      stroke={stroke}
      opacity={!fill || fill === 'none' || !backgroundFill ? opacityValue : undefined}
    />
  );
};
